<script>
import { db } from "@/services/firebase/firebase";
import moment from "moment";
import "moment/locale/es";
import {getTableRender} from "@/services/realtimeTable/realtimeTable.service";
import { getAvailabilities } from "@/services/disponibilidad/disponibilidad.service";

moment.locale("es");

export default {
  name: "RealtimeLeadsTable",
  props: {
    size: {
      type: Number,
      default: 10,
    },
    filter: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      page: 1,
      tableData: [],
      headers: [
        {
          text: "ID",
          align: "start",
          value: "originId",
        },
        {
          text: "Nombre",
          align: "start",
          value: "leadName",
        },
        {
          text: "Campaña",
          align: "start",
          value: "campaing",
        },
        {
          text: "Agente",
          align: "start",
          value: "agentName",
        },
        {
          text: "Agente signado",
          align: "start",
          value: "agentName",
        },
        {
          text: "Estatus",
          align: "start",
          value: "status",
        },
        {
          text: "Creado",
          align: "start",
          value: "date",
        },
      ],
      snackbar: {
        enabled: false,
        text: "",
        color: "success",
      },
      isInitialLoad: true,
      stats: {
        leadsHoy: 0,
        leadsAtendidos: 0,
        leadsEspera: 0,
        leadsMarcacion: 0,
        hourlyStats: {}
      },
      agentesDisponibles: 0,
      totalAgentesAvailability: 1
    };
  },
  async created() {
    try {
      const availabilities = await getAvailabilities();
      this.totalAgentesAvailability = availabilities.length || 1;
    } catch (e) {
      console.error("Error fetching availabilities:", e);
    }

    this.agentRef = db.ref("/agente");
    this.agentRef.on(
        "value",
        (snapshot) => {
          const data = snapshot.val() || {};
          this.agentesDisponibles = data.agentes_disponibles || 0;
        },
        (error) => {
          console.error("Firebase /agente error:", error);
        }
    );

    this.ref = db.ref("/leads");
    this.ref.on(
        "value",
        (snapshot) => {
          const data = snapshot.val() || {};
          const keys = Object.keys(data);
          // Sort keys by date descending (DD-MM-YYYY format)
          const sortedKeys = keys.sort((a, b) => {
            return moment(b, "DD-MM-YYYY").unix() - moment(a, "DD-MM-YYYY").unix();
          });
          const latestKey = sortedKeys[0];
          if (latestKey && data[latestKey]) {
            this.stats.leadsHoy = data[latestKey].leads_hoy || 0;
            this.stats.leadsAtendidos = data[latestKey].leads_atendidos || 0;
            this.stats.leadsEspera = data[latestKey].leads_espera || 0;
            this.stats.leadsMarcacion = data[latestKey].leads_marcacion || 0;
            this.stats.hourlyStats = data[latestKey].hourly_stats || {};
          }

          const latestLeads = (latestKey && data[latestKey].ultimos_leads)
              ? Object.values(data[latestKey].ultimos_leads)
              : [];

          const newData = keys.map((key) => ({
            leadsEspera: data[key].leads_espera,
            leadsHoy: data[key].leads_hoy,
            ultimos_leads: data[key].ultimos_leads,
            leadsAtendidos: data[key].leads_atendidos,
            leadsMarcacion: data[key].leads_marcacion,
            date: key
          }));

          // Detect new leads for alert (only after initial load)
          if (!this.isInitialLoad && newData.length > this.tableData.length) {
            this.snackbar.text = "¡Nuevo lead recibido!";
            this.snackbar.color = "success";
            this.snackbar.enabled = true;
          }

          this.fetchLatestLeads(latestLeads);
          this.isInitialLoad = false;
        },
        (error) => {
          console.error("Firebase error:", error);
        }
    );
  },
  beforeDestroy() {
    if (this.ref) {
      this.ref.off();
    }
    if (this.agentRef) {
      this.agentRef.off();
    }
  },
  methods: {
    async fetchLatestLeads(latestLeads){
      this.tableData = await getTableRender(latestLeads)
    },
    formatDate(timestamp) {
      if (!timestamp) return "N/A";
      return moment(timestamp).fromNow();
    },
    getStatusColor(status) {
      if (!status) return "#fafafa";
      const normalizedStatus = status.toLowerCase().trim();
      switch (normalizedStatus) {
        case "nuevo":
          return "#3FB89A";
        case "en espera":
          return "#FBA752";
        case "atendido":
          return "#0DA6C8";
        case "en llamada":
          return "#5DB1CF";
        case "finalizado":
          return "#2BA48F";
        default:
          return "#EEEEEE";
      }
    },
  },
  mounted() {
    // Forces ApexCharts to recalculate dimensions after Vuetify finishes layout
    setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 100);
  },
  computed: {
    leadsHoy() {
      return this.stats.leadsHoy;
    },
    leadsEnLlamada() {
      return this.tableData.filter(
          (lead) => (lead.status || "").toLowerCase().trim() === "en llamada"
      ).length;
    },
    leadsAtendidos() {
      return this.stats.leadsAtendidos;
    },
    leadsEnEspera() {
      return this.stats.leadsEspera;
    },
    options() {
      return {
        chart: {
          id: "RealtimeLeads",
          type: "area",
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        colors: ["#2BA48F", "#0DA6C8", "#3FB89A", "#FBA752"],
        xaxis: {
          categories: Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`),
          labels: {
            style: { colors: "#6a7c85" },
          },
        },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.45,
            opacityTo: 0.05,
            stops: [50, 100, 100]
          }
        },
        dataLabels: {
          enabled: false
        },
        tooltip: { theme: "light" },
      };
    },
    series() {
      const hourlyStats = this.stats.hourlyStats || {};
      const statuses = ['Nuevo', 'En llamada', 'Atendidos', 'En espera'];
      return statuses.map(status => {
        const data = Array.from({ length: 24 }, (_, hour) => {
          const hourKey = hour.toString().padStart(2, '0');
          return hourlyStats[hourKey]?.[status] || 0;
        });
        return {
          name: status,
          data: data
        };
      });
    },
    agentesDisponiblesPercentage() {
      if (!this.totalAgentesAvailability || this.totalAgentesAvailability === 0) return 0;
      return Math.round((this.agentesDisponibles / this.totalAgentesAvailability) * 100);
    },
    pages() {
      const totalPages = Math.ceil(this.currentItems.length / this.size);
      return totalPages < 1 ? 1 : totalPages;
    },
    currentItems() {
      let filtered = [...this.tableData];

      // Filter
      if (this.filter && this.filter.trim() !== "") {
        const searchTerm = this.filter.toLowerCase().trim();
        filtered = filtered.filter((lead) => {
          return (
              (lead.nombre || "").toLowerCase().includes(searchTerm) ||
              (lead.producto || "").toLowerCase().includes(searchTerm) ||
              (lead.correo || "").toLowerCase().includes(searchTerm) ||
              (lead.agentName || "").toLowerCase().includes(searchTerm) ||
              (lead.assignedAgent || "").toLowerCase().includes(searchTerm) ||
              (lead.status || "").toLowerCase().includes(searchTerm)
          );
        });
      }

      return filtered.sort((a, b) => {
        const dateA = moment(a.date).valueOf() || 0;
        const dateB = moment(b.date).valueOf() || 0;
        return dateB - dateA;
      });
    },
  },
};
</script>

<template>
  <div>
    <v-row class="mb-4 mt-2" dense>
      <v-col cols="12" sm="2" md="2" lg="2" xl="2">
        <v-card color="#2BA48F" dark class="rounded-lg stats-card">
          <v-card-text class="d-flex align-center pa-4">
            <v-icon size="40" class="mr-3 op-light">mdi-account-group</v-icon>
            <div>
              <div class="text-h4 font-weight-bold">{{ leadsHoy }}</div>
              <div class="caption">Leads nuevos hoy</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="2" md="2" lg="2" xl="2">
        <v-card color="#E5EEF5" class="rounded-lg stats-card" flat>
          <v-card-text class="d-flex align-center pa-4">
            <v-avatar color="#0DA6C8" size="40" class="mr-3">
              <v-icon color="white" small>mdi-phone</v-icon>
            </v-avatar>
            <div>
              <div class="text-h4 font-weight-bold primary-text">
                {{ leadsEnLlamada }}
              </div>
              <div class="caption secondary-text">Leads en llamada</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="2" md="2" lg="2" xl="2">
        <v-card color="#E5F3F1" class="rounded-lg stats-card" flat>
          <v-card-text class="d-flex align-center pa-4">
            <v-avatar color="#3FB89A" size="40" class="mr-3">
              <v-icon color="white" small>mdi-check-circle</v-icon>
            </v-avatar>
            <div>
              <div class="text-h4 font-weight-bold primary-text">
                {{ leadsAtendidos }}
              </div>
              <div class="caption secondary-text">Leads atendidos</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="2" md="2" lg="2" xl="2">
        <v-card color="#F9EFE7" class="rounded-lg stats-card" flat>
          <v-card-text class="d-flex align-center pa-4">
            <v-avatar color="#FBA752" size="40" class="mr-3">
              <v-icon color="white" small>mdi-timer-sand</v-icon>
            </v-avatar>
            <div>
              <div class="text-h4 font-weight-bold primary-text">
                {{ leadsEnEspera }}
              </div>
              <div class="caption secondary-text">Leads en espera</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="4" md="4" lg="4" xl="4">
        <v-card color="#E5EEF5" class="rounded-lg stats-card" flat>
          <v-card-text class="d-flex align-center pa-4">
            <v-avatar color="#0DA6C8" size="40" class="mr-3">
              <v-icon color="white">mdi-account-check</v-icon>
            </v-avatar>
            <div class="flex-grow-1">
              <div class="text-h4 font-weight-bold primary-text">{{ agentesDisponibles }}</div>
              <div class="caption secondary-text">Agentes disponibles</div>
            </div>
            <div class="progress-circle-container">
              <v-progress-circular
                  :value="agentesDisponiblesPercentage"
                  size="50"
                  width="4"
                  color="#0DA6C8"
                  class="ml-auto"
              >
                <span class="caption font-weight-bold primary-text">{{ agentesDisponiblesPercentage }}%</span>
              </v-progress-circular>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="header-table-style">
      <v-toolbar dense flat dark color="#093E4C">
        <v-toolbar-title>
          <span>Leads</span>
        </v-toolbar-title>
      </v-toolbar>
      <v-card-text>
        <div class="text-h6 font-weight-bold mb-4 primary-text">
          Actividad de Leads del Día
        </div>
        <div style="width: 100%; min-width: 0">
          <apexchart
              width="100%"
              height="250"
              type="area"
              :options="options"
              :series="series"
          ></apexchart>
        </div>
        <v-data-table
            :headers="headers"
            :items="currentItems"
            :page="page"
            :items-per-page="size"
            no-data-text="Sin agendas para mostrar"
            disable-sort
            hide-default-footer
            @update:page="page = $event"
        >
          <template v-slot:item.date="{ item }">
            {{ formatDate(item.date) }}
          </template>

          <template v-slot:item.assignedAgent="{ item }">
            <div class="d-flex align-center">
              <v-icon :color="getStatusColor(item.status)" class="mr-2">
                mdi-account
              </v-icon>
              <span>{{ item.assignedAgent }}</span>
            </div>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
                class="chip-status justify-center white--text font-weight-bold"
                :color="getStatusColor(item.status)"
                label
            >
              <span v-if="item.status"> {{ item.status  }}</span>
              <span v-else style="color:grey">Sin status</span>
            </v-chip>
          </template>

          <template
              v-slot:footer.page-text="{ pageStart, pageStop, itemsLength }"
          >
            <div v-if="itemsLength">
              Mostrando {{ pageStart }} - {{ pageStop }} de {{ itemsLength }} leads
            </div>
          </template>
          <template v-slot:item.view="{ item }">
            <v-btn icon small>
              <v-icon small @click="() => console.log(item)"> mdi-eye </v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions style="justify-content: center">
        <div
            style="display: flex; justify-content: center; align-items: center"
        >
          <v-pagination
              v-model="page"
              :length="pages"
              :total-visible="5"
              circle
              color="#0070C0"
          ></v-pagination>
        </div>
      </v-card-actions>
    </v-card>

    <v-snackbar
        v-model="snackbar.enabled"
        timeout="3000"
        right
        top
        :color="snackbar.color"
    >
      {{ snackbar.text }}

      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.enabled = false">
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<style>
.stats-card {
  height: 90px;
}
.primary-text {
  color: #093e4c;
}
.secondary-text {
  color: #6a7c85;
}
.op-light {
  opacity: 0.6;
}
.progress-circle-container {
  display: flex;
  align-items: center;
}
.chip-status {
  min-width: 120px;
}
.header-table-style {
  border-top-left-radius: 20px !important;
  border-top-right-radius: 20px !important;
}
</style>

